// Consolidated BESS widget for the Overview tab.
// Верхня секція — стиль EMS Box («УЗЕ · накопичувач»): показники ліворуч
// (іконка-квадрат + підпис + значення — той самий візуальний патерн, що й
// список станів заряду/розряду в EMS Box, але тут це РЕАЛЬНІ метрики, а не
// перемикач стану) + ілюстрація батарейки по центру + великий SoC-номер і
// PCS-потужність праворуч. Нижче — ще 3 плитки на всю ширину.
import i18n from '@/i18n';
import { BatteryCharging, BatteryLow, RefreshCw, Zap, ShieldCheck, Thermometer } from 'lucide-react';
import { SectionCard } from '@/components/scada/kit';
import BatteryViz from '@/components/scada/BatteryViz';
import { scada } from '@/scada/tokens';
import type { BessTelemetry } from '@/scada/mock/telemetry';

const n = (v?: number | null, d = 1) => (v == null ? '—' : Number(v).toFixed(d));

export default function BessConsolidated({ bess, capacityKwh, fill }: { bess: BessTelemetry; capacityKwh?: number; fill?: boolean }) {
  const modeLabel = bess.mode === 'charging' ? i18n.t('ak_scada_charge') : bess.mode === 'discharging' ? i18n.t('ak_scada_discharge') : bess.mode === 'fault' ? i18n.t('ak_scada_alarm') : i18n.t('ak_scada_standby_3');
  const modeColor = bess.mode === 'charging' ? scada.state.curtailed : bess.mode === 'discharging' ? scada.state.ok : bess.mode === 'fault' ? scada.state.alarm : scada.textDim;
  const soc = bess.socPct ?? 0;
  const charging = bess.mode === 'charging';
  const fault = bess.mode === 'fault';

  return (
    <SectionCard
      fill={fill}
      title={i18n.t('ak_scada_bess_storage')}
      subtitle={capacityKwh ? i18n.t('ak_scada_mwh_2', { v: (capacityKwh / 1000).toFixed(3).replace('.', i18n.language === 'en' ? '.' : ',') }) : i18n.t('ak_scada_consolidated_bess_status')}
      right={<span style={{ color: modeColor, fontSize: 12, fontWeight: 600 }}>{modeLabel}</span>}
    >
      <div className="flex flex-col justify-center" style={{ height: '100%', gap: 16 }}>
        {/* Top: показники (іконка-квадрат + підпис + значення) + батарейка + SoC/PCS */}
        <div className="flex" style={{ gap: 8 }}>
          <div className="flex justify-center items-center" style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { key: 'chargeAvail', label: i18n.t('ak_scada_to_charge'), Icon: BatteryCharging, value: `${n(bess.chargeAvailableKwh, 0)} ${i18n.t('ak_scada_kwh')}` },
                { key: 'dischargeAvail', label: i18n.t('ak_scada_to_discharge'), Icon: BatteryLow, value: `${n(bess.dischargeAvailableKwh, 0)} ${i18n.t('ak_scada_kwh')}` },
                // Лічильники Kehua за поточну добу (сума по 16 PCS), скидаються
                // опівночі. Без слів «за добу» підпис читався як миттєва
                // потужність — тому вони тут обов'язкові.
                { key: 'daily', label: i18n.t('ak_scada_charge_discharge_per_day'), Icon: RefreshCw, value: `${n(bess.dailyChargeKwh, 0)}/${n(bess.dailyDischargeKwh, 0)} ${i18n.t('ak_scada_kwh')}` },
              ].map((m) => (
                <div key={m.key} className="flex items-center" style={{ gap: 10 }}>
                  <span style={{
                    width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: scada.panel2,
                  }}>
                    <m.Icon size={17} strokeWidth={2} style={{ color: scada.text }} />
                  </span>
                  <div>
                    <div style={{ fontSize: 10.5, color: scada.textDim }}>{m.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: scada.text }}>{m.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center items-center" style={{ flex: 1, minWidth: 0 }}>
            <BatteryViz soc={soc} charging={charging} fault={fault} h={196} />
          </div>
          <div className="flex flex-col items-center justify-center" style={{ flex: 1, minWidth: 0, gap: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div className="flex items-baseline justify-center" style={{ gap: 3 }}>
                <span className="font-display" style={{ fontSize: 44, fontWeight: 700, lineHeight: 1, color: scada.text }}>{soc == null ? '—' : n(soc, 0)}</span>
                <span style={{ fontSize: 18, color: scada.textDim }}>%</span>
              </div>
              <div style={{ fontSize: 11, color: scada.textDim, marginTop: 5, letterSpacing: '.05em' }}>{i18n.t('ak_scada_soc_level')}</div>
            </div>
            <div className="flex items-center justify-center" style={{ gap: 7 }}>
              <Zap size={15} strokeWidth={1.9} style={{ color: scada.text }} />
              <span style={{ fontSize: 12, color: scada.textDim }}>PCS</span>
              <span style={{ color: scada.textDim }}>·</span>
              <span className="font-display" style={{ fontSize: 15, fontWeight: 600, color: scada.text }}>
                {n(Math.abs(bess.powerKw), 1)}<span style={{ fontSize: 11, color: scada.textDim, fontWeight: 400 }}> {i18n.t('ak_scada_kw')}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Bottom: iконка+пiдпис+значення, як i верхнi показники, замiсть
            прямокутних плиток - монохромно, без боксу. */}
        <div className="grid grid-cols-3 gap-2" style={{ paddingTop: 10, borderTop: `1px solid ${scada.border}` }}>
          {[
            { key: 'soh', label: 'SOH', Icon: ShieldCheck, value: `${n(bess.sohPct, 0)} %` },
            { key: 'voltage', label: i18n.t('ak_scada_voltage'), Icon: Zap, value: `${n(bess.batteryV, 0)} ${i18n.t('ak_scada_v')}` },
            { key: 'temp', label: i18n.t('ak_scada_temperature'), Icon: Thermometer, value: `${n(bess.innerTempC, 0)} °C` },
          ].map((m) => (
            <div key={m.key} className="flex items-center" style={{ gap: 8 }}>
              <span style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: scada.panel2,
              }}>
                <m.Icon size={15} strokeWidth={2} style={{ color: scada.text }} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 9.5, color: scada.textDim }}>{m.label}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: scada.text }}>{m.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionCard>
  );
}
